import type { CatalogProps } from "@core/FileStructue/Catalog/CatalogProps";
import type { ReadonlyCatalog } from "@core/FileStructue/Catalog/ReadonlyCatalog";
import type { Category } from "@core/FileStructue/Category/Category";
import type { Item } from "@core/FileStructue/Item/Item";
import { ItemType } from "@core/FileStructue/Item/ItemType";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import { isRootLanguageCategory } from "@ext/localization/core/catalogExt";
import t from "@ext/localization/locale/translate";

export type CatalogBehavior = {
	/** Unlike doc-root `hidden`, it keeps the catalog's link and its articles open by it. */
	unlisted?: boolean;
	readOnlyTopCategories?: boolean;
	noRootArticles?: boolean;
	lockedArticleUrl?: boolean;
	/** Called once per new article; its copies in other languages take the same name. */
	newArticleFileName?: (parent: Category) => string;
	/** The catalog menu keeps only its checks and the items others add to it; the article menu, only deleting. */
	limitedActions?: boolean;
	/** Asked at every read of a top-level category's title, so the answer follows the current language; never saved. */
	topCategoryTitle?: (folderName: string) => string | undefined;
	/** Like `topCategoryTitle`; an assigned title is dropped. */
	catalogTitle?: () => string;
};

export type ClientCatalogBehavior = {
	readOnlyTopCategories: boolean;
	noRootArticles: boolean;
	lockedArticleUrl: boolean;
	namedNewArticles: boolean;
	limitedActions: boolean;
};

export const toClientCatalogBehavior = (behavior: CatalogBehavior): ClientCatalogBehavior => ({
	readOnlyTopCategories: !!behavior.readOnlyTopCategories,
	noRootArticles: !!behavior.noRootArticles,
	lockedArticleUrl: !!behavior.lockedArticleUrl,
	namedNewArticles: !!behavior.newArticleFileName,
	limitedActions: !!behavior.limitedActions,
});

/** A multilingual catalog has a top level in every language. */
const isTopLevelFolder = (catalog: ReadonlyCatalog, item: Item): boolean =>
	item === catalog.getRootCategory() || isRootLanguageCategory(catalog, item);

const isTopLevelCategory = (catalog: ReadonlyCatalog, item: Item | undefined): boolean =>
	item?.type === ItemType.category &&
	!!item.parent &&
	isTopLevelFolder(catalog, item.parent) &&
	!isRootLanguageCategory(catalog, item);

export const isReadOnlyItem = (catalog: ReadonlyCatalog, item: Item | undefined): boolean =>
	!!catalog.behavior.readOnlyTopCategories && isTopLevelCategory(catalog, item);

export const showTopCategoryTitle = (catalog: ReadonlyCatalog, category: Category): void => {
	const title = catalog.behavior.topCategoryTitle;
	if (!title) return;
	category.setShownTitle(() => (isTopLevelCategory(catalog, category) ? title(category.getFileName()) : undefined));
};

/** A catalog that names new articles names their files by ids; a folder with no index file keeps showing its name. */
export const markIdFileName = (catalog: ReadonlyCatalog | undefined, item: Item, hasOwnFile: boolean): void => {
	if (hasOwnFile && catalog?.behavior.newArticleFileName) item.markFileNameAsId();
};

/** Every reader takes the catalog's title from its props, so `catalogTitle` answers there. */
export const showCatalogTitle = (behavior: CatalogBehavior | undefined, props: CatalogProps): CatalogProps => {
	const title = behavior?.catalogTitle;
	if (title)
		Object.defineProperty(props, "title", {
			get: title,
			set: () => undefined,
			enumerable: true,
			configurable: true,
		});
	return props;
};

export const assertItemChangeable = (catalog: ReadonlyCatalog, item: Item | undefined): void => {
	if (isReadOnlyItem(catalog, item)) throw new DefaultError(t("catalog.error.read-only-item"));
};

/** Nesting under a top-level article turns it into a top-level category, which `readOnlyTopCategories` locks. */
export const assertCanNestUnder = (catalog: ReadonlyCatalog, item: Item): void => {
	const isTopLevelArticle = item.type === ItemType.article && !!item.parent && isTopLevelFolder(catalog, item.parent);
	if (catalog.behavior.readOnlyTopCategories && isTopLevelArticle)
		throw new DefaultError(t("catalog.error.top-level-article-nesting"));
};

/** `readOnlyTopCategories` keeps the groups there are: none can be added, not even by a move. */
const assertCanAddGroupIn = (catalog: ReadonlyCatalog, parent: Item): void => {
	if (catalog.behavior.readOnlyTopCategories && isTopLevelFolder(catalog, parent))
		throw new DefaultError(t("catalog.error.no-new-groups"));
};

const assertCanPlaceIn = (catalog: ReadonlyCatalog, parent: Item): void => {
	if (catalog.behavior.noRootArticles && isTopLevelFolder(catalog, parent))
		throw new DefaultError(t("catalog.error.no-root-articles"));
};

export const assertFileNameKept = (catalog: ReadonlyCatalog, item: Item, fileName: string | undefined): void => {
	if (catalog.behavior.lockedArticleUrl && fileName && fileName !== item.getFileName())
		throw new DefaultError(t("catalog.error.locked-article-url"));
};

/** An article parent turns into a category to take the item. */
export const assertCanAddTo = (catalog: ReadonlyCatalog, parent: Item, type: ItemType): void => {
	if (parent.type === ItemType.article) {
		assertCanNestUnder(catalog, parent);
		return;
	}
	assertCanPlaceIn(catalog, parent);
	if (type === ItemType.category) assertCanAddGroupIn(catalog, parent);
};

export type ItemMove = {
	source: ReadonlyCatalog;
	item: Item;
	target?: ReadonlyCatalog;
	/** Where the item lands; without it only the item itself is checked. */
	parent?: Item;
	/** What the item arrives as: an article moved to a category path becomes a category. */
	asType?: ItemType;
	fileName?: string;
};

export const assertCanMoveItem = ({
	source,
	item,
	target = source,
	parent,
	asType = item.type,
	fileName,
}: ItemMove): void => {
	assertItemChangeable(source, item);
	if (asType !== item.type) assertCanNestUnder(source, item);
	assertFileNameKept(source, item, fileName);
	if (parent) assertCanAddTo(target, parent, asType);
};
